import * as React from 'react';
import { usePermissions, useTranslate } from 'react-admin';
import { Paper } from '@material-ui/core';
import { MaterialTable } from '~/components';
import OperationTypeInput from '~/components/OperationTypeInput';
import * as hb from '~/types/Handbook';
import {
  composeValidators,
  createHelperText,
  validateScreamingSnakeCase,
  validateUniqueByCustomRule,
} from './utils';

const ALLOWED_OPERATION_TYPES = new Set([
  'INCOMING_TRANSFER',
  'SERVICE_CHARGE',
  'ACCOUNT_TRANSFER',
  'CASH_DEPOSIT',
  'CARD_TRANSACTION',
  'DEBIT_ORDER',
]);

const OperationSubtypeTable = ({
  handbook,
  setState,
}: {
  handbook: Partial<hb.Handbook>;
  setState: hb.SetState;
}) => {
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const t = (key: string) =>
    translate(`resources.solutions.handbooks.operationSubtypesColumns.${key}`);

  const operationSubtypes = (handbook.operationSubtypes || []).slice();

  const operationTypeChoices = (OperationTypeInput.getChoices(translate, false) as {
    value: string;
    text: string;
  }[]).filter((c) => c.value !== '');

  const getLabelForOperationType = (value: string) => {
    const choice = operationTypeChoices.find((c) => c.value === value);
    return choice ? (choice.text as string) : value;
  };

  if (!permissions) return null;

  return (
    <MaterialTable
      columns={[
        {
          title: t('operationType'),
          field: 'operationType',
          required: true,
          validate: (rowData: any) => {
            if (rowData?.tableData?.editing === 'delete') return true;
            if (!rowData.operationType) return t('required');
            return true;
          },
          render: (rowData: hb.OperationSubtype) => getLabelForOperationType(rowData.operationType),
          editComponent: (props: any) => {
            const errorMessage = props.columnDef.validate
              ? props.columnDef.validate(props.rowData)
              : null;
            const helperText = createHelperText(errorMessage);
            return (
              <OperationTypeInput
                value={props.value || ''}
                onChange={(e: any) => props.onChange(e.target.value)}
                optionsFilter={(o: any) => ALLOWED_OPERATION_TYPES.has(o.value)}
                error={typeof errorMessage === 'string'}
                helperText={helperText}
              />
            );
          },
        },
        {
          title: t('operationSubtype'),
          field: 'operationSubtype',
          required: true,
          validate: composeValidators(
            validateScreamingSnakeCase('operationSubtype', t('required'), t('screamingSnakeCase')),
            validateUniqueByCustomRule<hb.OperationSubtype>(
              operationSubtypes,
              (a, b) =>
                a.operationType === b.operationType && a.operationSubtype === b.operationSubtype,
              t('duplicatePair')
            )
          ),
        },
      ]}
      components={{
        Container: (props: any) => <Paper variant='outlined'>{props.children}</Paper>,
      }}
      data={operationSubtypes}
      editable={
        permissions?.includes('solution.handbook.update') && {
          isEditable: (rowData: hb.OperationSubtype) => !rowData.readonly,
          isDeletable: (rowData: hb.OperationSubtype) => !rowData.readonly,
          onRowAdd: (newData: hb.OperationSubtype) => {
            newData.readonly = false;
            operationSubtypes.push(newData);
            return setState({ operationSubtypes });
          },
          onRowUpdate: (newData: hb.OperationSubtype, oldData: hb.OperationSubtype) => {
            const index = operationSubtypes.indexOf(oldData);
            operationSubtypes[index] = newData;
            return setState({ operationSubtypes });
          },
          onRowDelete: (oldData: hb.OperationSubtype) => {
            const index = operationSubtypes.indexOf(oldData);
            operationSubtypes.splice(index, 1);
            return setState({ operationSubtypes });
          },
        }
      }
      options={{
        actionsColumnIndex: -1,
        emptyRowsWhenPaging: false,
        showTitle: false,
        addRowPosition: 'first',
        paging: false,
      }}
    />
  );
};

export default OperationSubtypeTable;
